/**
 * Upload a body directly to GCS via a signed PUT URL.
 * Uses XMLHttpRequest for real upload progress tracking.
 * contentType must equal the type the URL was signed with.
 */
export function putToGCS(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    const abortHandler = () => {
      xhr.abort();
      reject(new Error("Upload cancelled"));
    };

    signal.addEventListener("abort", abortHandler);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };

    xhr.onload = () => {
      signal.removeEventListener("abort", abortHandler);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`GCS upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      signal.removeEventListener("abort", abortHandler);
      reject(new Error("Network error during upload to storage"));
    };

    xhr.onabort = () => {
      signal.removeEventListener("abort", abortHandler);
      reject(new Error("Upload cancelled"));
    };

    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(body);
  });
}
